// Self-contained scroll-carousel primitives (Go Group).
//
// Provides the three custom elements the scroll-area carousel markup relies on
// — <scroll-carousel>, <scroll-progress observes="id"> and the prev/next
// customized <button> controls — without pulling in the Horizon carousel class
// hierarchy. The horizontal scroll + snap layout itself is done in CSS
// (`.scroll-area`); these elements only wire progress tracking and the arrows.

/**
 * requestAnimationFrame-coalesced wrapper so scroll handlers run at most once
 * per frame.
 * @param {() => void} fn
 * @returns {() => void}
 */
function rafThrottle(fn) {
  let ticking = false;
  return () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      fn();
    });
  };
}

// <scroll-carousel>: scroll-snap container. Layout is handled by CSS; we only
// register it so the element hydrates as a known element.
class ScrollCarousel extends HTMLElement {}

if (!customElements.get('scroll-carousel')) {
  customElements.define('scroll-carousel', ScrollCarousel);
}

// <scroll-progress observes="id">: reflects the observed element's horizontal
// scroll position as a `--scroll-progress` custom property (0..1).
class ScrollProgress extends HTMLElement {
  /** @type {HTMLElement | null} */
  #scrolled = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {() => void} */
  #update = () => {};

  connectedCallback() {
    const el = this.scrolledElement;
    if (!el) return;
    this.#update = rafThrottle(() => this.#updateProgress());
    el.addEventListener('scroll', this.#update, { passive: true });
    if (window.ResizeObserver) {
      this.#resizeObserver = new ResizeObserver(this.#update);
      this.#resizeObserver.observe(el);
    }
    this.#updateProgress();
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
  }

  /** @returns {HTMLElement | null} */
  get scrolledElement() {
    if (!this.#scrolled) {
      this.#scrolled = document.getElementById(this.getAttribute('observes') || '');
    }
    return this.#scrolled;
  }

  #updateProgress() {
    const el = this.scrolledElement;
    if (!el) return;
    const scrollLeft = document.dir === 'rtl' ? Math.abs(el.scrollLeft) : el.scrollLeft;
    const advancement = (scrollLeft + el.clientWidth) / el.scrollWidth;
    this.style.setProperty('--scroll-progress', Math.max(0, Math.min(advancement, 1)).toFixed(6));
  }
}

if (!customElements.get('scroll-progress')) {
  customElements.define('scroll-progress', ScrollProgress);
}

// Prev/next controls: scroll the element referenced by aria-controls by ~one
// viewport width and reflect the edge state via [disabled].
class ScrollButton extends HTMLButtonElement {
  /** @type {() => void} */
  #sync = () => {};
  /** @type {() => void} */
  #onClick = () => {};

  connectedCallback() {
    this.#onClick = () => this.#scroll();
    this.addEventListener('click', this.#onClick);
    const el = this.controlledElement;
    if (el) {
      this.#sync = rafThrottle(() => this.#syncDisabled());
      el.addEventListener('scroll', this.#sync, { passive: true });
      if (window.ResizeObserver) new ResizeObserver(this.#sync).observe(el);
      this.#syncDisabled();
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @returns {HTMLElement | null} */
  get controlledElement() {
    const id = this.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  /** @returns {number} -1 for previous, 1 for next. */
  get direction() {
    return 1;
  }

  #scroll() {
    const el = this.controlledElement;
    if (!el) return;
    const sign = document.dir === 'rtl' ? -1 : 1;
    el.scrollBy({ left: el.clientWidth * 0.9 * this.direction * sign, behavior: 'smooth' });
  }

  #syncDisabled() {
    const el = this.controlledElement;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const left = Math.abs(el.scrollLeft);
    this.disabled = this.direction < 0 ? left <= 1 : left >= max - 1;
  }
}

class PrevButton extends ScrollButton {
  get direction() {
    return -1;
  }
}

class NextButton extends ScrollButton {
  get direction() {
    return 1;
  }
}

if (!customElements.get('prev-button')) {
  customElements.define('prev-button', PrevButton, { extends: 'button' });
}

if (!customElements.get('next-button')) {
  customElements.define('next-button', NextButton, { extends: 'button' });
}
